import React from 'react';
import { useAppStore } from '../store/useAppStore';
import { FileText, Image as ImageIcon, Activity, Plus, Layers, GraduationCap, ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { formatDate } from '../lib/utils';

const SimpleStat = ({ title, value, color }: { title: string, value: string | number, color: string }) => (
  <div className="bg-white p-4 rounded-xl border border-slate-200">
    <p className="text-xs font-medium text-slate-500 mb-1">{title}</p>
    <h3 className={`text-xl font-bold ${color}`}>{value}</h3>
  </div>
);

const DashboardPage = () => {
  const { user, pdfs, images, getStats } = useAppStore();
  const stats = getStats();
  const navigate = useNavigate();

  const recentImages = images.slice(0, 4);

  return (
    <div className="space-y-10">
      <section>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Welcome, {user?.name || 'Researcher'}</h1>
            <p className="text-slate-500 text-sm">You have {stats.totalImages} histology slides in your library.</p>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-1 no-scrollbar">
            <SimpleStat title="Total Slides" value={stats.totalImages} color="text-primary-500" />
            <SimpleStat title="Accuracy" value={`${Math.round(stats.avgScore)}%`} color="text-emerald-500" />
            <SimpleStat title="Sessions" value={stats.totalSessions} color="text-indigo-500" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Quick Study Card */}
          <Link to="/flashcards" className="group bg-primary-600 rounded-2xl p-6 text-white flex flex-col justify-between hover:bg-primary-700 transition-all shadow-lg shadow-primary-500/10">
            <div>
              <Layers className="h-8 w-8 mb-4 opacity-80" />
              <h3 className="text-xl font-bold mb-2">Resume Flashcards</h3>
              <p className="text-primary-100 text-sm leading-relaxed max-w-xs">Study your global deck to maintain active recall mastery.</p>
            </div>
            <div className="mt-8 flex items-center text-sm font-bold uppercase tracking-wider group-hover:translate-x-1 transition-transform">
              Start Studying <ChevronRight className="h-4 w-4 ml-1" />
            </div>
          </Link>

          {/* Quick Upload Card */}
          <Link to="/upload" className="group bg-white rounded-2xl p-6 border border-slate-200 flex flex-col justify-between hover:border-primary-300 transition-all">
            <div>
              <Plus className="h-8 w-8 mb-4 text-primary-500" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Extract New Data</h3>
              <p className="text-slate-500 text-sm leading-relaxed max-w-xs">Upload a new lecture PDF to add slides to your collection.</p>
            </div>
            <div className="mt-8 flex items-center text-sm font-bold text-primary-600 uppercase tracking-wider group-hover:translate-x-1 transition-transform">
              Open Uploader <ChevronRight className="h-4 w-4 ml-1" />
            </div>
          </Link>
        </div>
      </section>

      {/* Recent Slides Section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">Recent Slides</h2>
          <Link to="/library" className="text-sm font-medium text-primary-600 hover:underline">View Library</Link>
        </div>
        
        {recentImages.length === 0 ? (
          <div className="bg-slate-100/50 border border-slate-200 border-dashed rounded-2xl p-12 text-center">
            <p className="text-slate-400 text-sm">Upload a PDF to see your slides here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {recentImages.map(img => (
              <div 
                key={img.id} 
                onClick={() => navigate('/library')}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow group"
              >
                <div className="aspect-square bg-slate-100 relative overflow-hidden">
                  <img src={img.image_url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <p className="font-semibold text-slate-900 text-sm truncate">{img.identified_organ_tissue}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default DashboardPage;